/**
 * Microphone Testing Page - User-facing microphone and speech recognition testing
 * 
 * This page provides:
 * - Real-time microphone status and feedback
 * - Speech recognition testing for all available engines
 * - Live transcript display
 * - Engine availability and performance testing
 * - Clean, user-friendly interface
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { EnhancedSpeechManager, MicManager, probe, getProbeStats } from '../services/speech';
import ChefVoiceControls from '../components/ChefVoiceControls';
import ChefVoiceSettings from '../components/ChefVoiceSettings';
import { Modal, Heading, Text } from '../components/ui';
import { useSimpleMountGuard } from '../services/speech/SimpleMountGuard.js';

const ChefVoiceDemo = () => {
    const { user } = useAuth();
    const [speechManager, setSpeechManager] = useState(null);
    const [isEnabled, setIsEnabled] = useState(false);
    
    // Use SimpleMountGuard for StrictMode safety
    const mountGuard = useSimpleMountGuard('ChefVoiceDemo');
    const [showSettings, setShowSettings] = useState(false);
    const [micOn, setMicOn] = useState(false);
    const [transcripts, setTranscripts] = useState([]);
    const [pendingTranscripts, setPendingTranscripts] = useState([]);
    const [engineStats, setEngineStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Real-time status and feedback
    const [statusMessage, setStatusMessage] = useState('Initializing microphone testing...');
    const [currentEngine, setCurrentEngine] = useState('Unknown');
    const [engineStatuses, setEngineStatuses] = useState({});
    const [liveTranscript, setLiveTranscript] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [triggerDetected, setTriggerDetected] = useState(false);
    const [kwsStatus, setKwsStatus] = useState({ enabled: false, ready: false, wakePhrase: 'hey chef', lastScore: 0, type: 'none' });

    // Mock recipe for testing phrase hints
    const mockRecipe = {
        id: 'demo_recipe_123',
        title: 'Chocolate Chip Cookies',
        ingredients: [
            '2 cups all-purpose flour',
            '1 cup butter, softened',
            '3/4 cup brown sugar',
            '1/2 cup white sugar',
            '2 large eggs',
            '2 teaspoons vanilla extract',
            '1 teaspoon baking soda',
            '1 teaspoon salt',
            '2 cups chocolate chips'
        ],
        steps: [
            {
                instructions: 'Preheat oven to 375°F (190°C).'
            },
            {
                instructions: 'Cream together butter and both sugars until smooth.'
            },
            {
                instructions: 'Beat in eggs one at a time, then stir in vanilla.'
            },
            {
                instructions: 'Mix in flour, baking soda, and salt.'
            },
            {
                instructions: 'Fold in chocolate chips.'
            },
            {
                instructions: 'Drop rounded tablespoons onto ungreased cookie sheets.'
            },
            {
                instructions: 'Bake for 9 to 11 minutes until golden brown.'
            }
        ]
    };

    // Monitor mic state
    useEffect(() => {
        const updateMicState = () => {
            const state = MicManager.getState();
            setMicOn(state.isAcquired && state.audioContextState === 'running');
        };

        updateMicState();
        const interval = setInterval(updateMicState, 100); // Update 10 times per second
        return () => clearInterval(interval);
    }, []);

    // Monitor KWS status
    useEffect(() => {
        const updateKWSStatusInterval = () => {
            updateKWSStatus();
        };

        updateKWSStatusInterval();
        const interval = setInterval(updateKWSStatusInterval, 500); // Update twice per second
        return () => clearInterval(interval);
    }, [speechManager]);

    // Initialize speech manager with MountGuard
    useEffect(() => {
        let isMounted = true;
        
        const initializeSpeechManager = async () => {
            try {
                setIsLoading(true);
                setError(null);
                setStatusMessage('Initializing speech recognition engines...');

                const manager = new EnhancedSpeechManager({
                    useSmartEngine: true,
                    enablePhraseHints: true,
                    enableTranscripts: true,
                    enablePushToTalk: true,
                    requireConfirmation: true,
                    showAllResults: false, // Gate results by trigger
                    enableKWS: true, // Enable KWS for wake word detection
                    wakePhrase: 'hey chef', // Default wake phrase
                    sessionId: 'demo_session_' + Date.now()
                });

                // Set recipe context for phrase hints
                manager.setRecipeContext(mockRecipe);

                // Only set state if still mounted
                if (isMounted) {
                    setSpeechManager(manager);
                    setIsLoading(false);
                    setStatusMessage('Speech recognition ready. Click "Enable Microphone Testing" to begin.');
                    
                    // Defer active engine probes until user opens settings
                    setStatusMessage('Ready. Click Turn On Mic to start.');
                } else {
                    // Component unmounted during initialization, cleanup
                    manager.destroy();
                }
            } catch (err) {
                console.error('Failed to initialize speech manager:', err);
                if (isMounted) {
                    setError('Failed to initialize voice features. Please check your browser permissions.');
                    setStatusMessage(`Error: ${err.message}`);
                    setIsLoading(false);
                }
            }
        };

        initializeSpeechManager();

        return () => {
            isMounted = false;
            if (speechManager) {
                speechManager.destroy();
            }
        };
    }, []);

    // Update stats periodically
    useEffect(() => {
        if (!speechManager) return;

        const updateStats = () => {
            try {
                setEngineStats(speechManager.getEngineStats());
                setTranscripts(speechManager.getRecentTranscripts(10));
                setPendingTranscripts(speechManager.getPendingTranscripts());
            } catch (err) {
                console.error('Failed to update stats:', err);
            }
        };

        updateStats();
        const interval = setInterval(updateStats, 2000);

        return () => clearInterval(interval);
    }, [speechManager]);

    // Handle transcript received
    const handleTranscriptReceived = (transcript) => {
        setPendingTranscripts(prev => [transcript, ...prev.slice(0, 4)]);
        console.log('New transcript received:', transcript);
        
        // Check if this is a trigger phrase
        if (transcript.toLowerCase().includes('hey chef')) {
            setTriggerDetected(true);
            setStatusMessage('🎯 Trigger phrase detected! Command received.');
            setTimeout(() => setTriggerDetected(false), 3000);
        }
        
        setLiveTranscript(transcript);
        setStatusMessage(`Transcript: "${transcript}"`);
    };

    // Handle transcript confirmation
    const handleConfirmTranscript = (transcriptId) => {
        if (speechManager) {
            const success = speechManager.confirmTranscript(transcriptId);
            if (success) {
                setPendingTranscripts(prev => prev.filter(t => t.id !== transcriptId));
                setTranscripts(prev => speechManager.getRecentTranscripts(10));
            }
        }
    };

    // Handle transcript rejection
    const handleRejectTranscript = (transcriptId) => {
        if (speechManager) {
            speechManager.rejectTranscript(transcriptId);
            setPendingTranscripts(prev => prev.filter(t => t.id !== transcriptId));
        }
    };

    // Handle settings change
    const handleSettingsChange = (settings) => {
        console.log('Settings changed:', settings);
        // Settings are automatically applied to the speech manager
    };

    // Update KWS status
    const updateKWSStatus = () => {
        if (speechManager && speechManager.smartEngine) {
            const status = speechManager.smartEngine.getKWSStatus();
            setKwsStatus(status);
        }
    };

    // Test engine availability
    const testEngineAvailability = async () => {
        try {
            const engines = ['browser', 'vad', 'streaming'];
            const statuses = {};
            
            for (const engine of engines) {
                setStatusMessage(`Testing ${engine} engine...`);
                const result = await probe(engine);
                statuses[engine] = {
                    available: result,
                    status: result ? 'available' : 'unavailable'
                };
            }
            
            setEngineStatuses(statuses);
            
            // Determine current engine
            const availableEngines = Object.entries(statuses).filter(([_, status]) => status.available);
            if (availableEngines.length > 0) {
                setCurrentEngine(availableEngines[0][0]);
                setStatusMessage(`Ready! ${availableEngines.length} engine(s) available. Current: ${availableEngines[0][0]}`);
            } else {
                setStatusMessage('Warning: No speech engines available. Check browser permissions.');
            }
            
            // Update KWS status
            updateKWSStatus();
        } catch (error) {
            console.error('Engine availability test failed:', error);
            setStatusMessage(`Engine test failed: ${error.message}`);
        }
    };

    const handleToggleEnabled = async (enabled) => {
        if (!speechManager) return;
        
        try {
            if (enabled) {
                setStatusMessage('Starting microphone...');
                // Ensure AC is resumed to satisfy autoplay policy
                try { await MicManager.resume(); } catch (_) {}
                await speechManager.start();
                setIsEnabled(true);
                setStatusMessage('Microphone active. Say "hey chef" to test trigger detection.');
            } else {
                setStatusMessage('Stopping microphone...');
                // Use hardStop to guarantee mic off
                await speechManager.hardStop('user_toggle_off');
                setIsEnabled(false);
                setStatusMessage('Microphone stopped.');
                // Update mic state immediately
                const state = MicManager.getState();
                setMicOn(state.isAcquired && state.audioContextState === 'running');
            }
        } catch (error) {
            console.error('Failed to toggle speech manager:', error);
            setError(error.message);
            setStatusMessage(`Error: ${error.message}`);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
                <div className="text-center">
                    <Heading level={1} style={{ color: '#1b263b' }}>
                        Login Required
                    </Heading>
                    <Text className="text-neutral-900 opacity-60 mt-2">
                        Please log in to test Chef voice features
                    </Text>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
                <div className="text-center">
                    <Heading level={1} style={{ color: '#1b263b' }}>
                        Initializing Microphone Testing...
                    </Heading>
                    <Text className="text-neutral-900 opacity-60 mt-2">
                        {statusMessage}
                    </Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <Heading level={1} style={{ color: '#1b263b' }}>
                        Voice Setup Error
                    </Heading>
                    <Text className="text-neutral-900 opacity-60 mt-2">
                        {error}
                    </Text>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity font-mono text-sm uppercase tracking-wider"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Heading level={1} style={{ color: '#1b263b' }}>
                        Microphone Testing
                    </Heading>
                    <Text className="text-neutral-900 opacity-60 mt-2">
                        Test speech recognition engines and microphone functionality
                    </Text>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column - Controls and Status */}
                    <div className="space-y-6">
                        {/* Quick Status */}
                        <div className="bg-neutral-0 hairline p-6">
                            <div className="flex items-center justify-between mb-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    Quick Status
                                </Text>
                                <button
                                    onClick={() => setShowSettings(true)}
                                    className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity"
                                >
                                    Settings
                                </button>
                            </div>
                            
                            <div className="flex items-center justify-between mb-2">
                                <Text className="text-sm text-neutral-900">Status</Text>
                                <div className="flex items-center">
                                    <div className={`w-2 h-2 rounded-full mr-2 ${
                                        micOn ? 'bg-red-500 animate-pulse' : 'bg-gray-300'
                                    }`} />
                                    <Text className="text-xs font-mono uppercase tracking-wider">
                                        {micOn ? 'MIC ON' : 'MIC OFF'}
                                    </Text>
                                </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <Text className="text-sm text-neutral-900">Engine</Text>
                                <Text className="text-xs font-mono uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    {currentEngine}
                                </Text>
                            </div>
                            
                            {/* KWS Status */}
                            {kwsStatus.enabled && (
                                <div className="flex items-center justify-between">
                                    <Text className="text-sm text-neutral-900">Wake Phrase</Text>
                                    <div className="flex items-center">
                                        <div className={`w-2 h-2 rounded-full mr-2 ${
                                            kwsStatus.ready ? 'bg-green-500' : 'bg-gray-300'
                                        }`} />
                                        <Text className="text-xs font-mono uppercase tracking-wider">
                                            {kwsStatus.wakePhrase}
                                        </Text>
                                    </div>
                                </div>
                            )}
                            
                            {/* Debug KWS Status (dev only) */}
                            {process.env.NODE_ENV === 'development' && kwsStatus.enabled && (
                                <div className="text-xs text-neutral-600 font-mono">
                                    KWS: {kwsStatus.ready ? 'ready' : 'not ready'} | score: {kwsStatus.lastScore.toFixed(3)} | type: {kwsStatus.type}
                                </div>
                            )}
                        </div>

                        {/* Voice Controls */}
                        <div className="bg-neutral-0 hairline p-6">
                            <ChefVoiceControls
                                speechManager={speechManager}
                                isEnabled={isEnabled}
                                onToggleEnabled={handleToggleEnabled}
                                onTranscriptReceived={handleTranscriptReceived}
                                currentRecipe={mockRecipe}
                                setMicOn={setMicOn}
                            />
                        </div>

                        {/* Trigger Detection Alert */}
                        {triggerDetected && (
                            <div className="bg-green-50 border border-green-200 rounded p-4">
                                <Text className="text-sm font-medium text-green-800 text-center">
                                    🎯 Trigger phrase detected!
                                </Text>
                            </div>
                        )}

                    </div>

                    {/* Right Column - Live Transcripts */}
                    <div className="space-y-6">
                        {/* Live Transcript Display */}
                        <div className="bg-neutral-0 hairline p-6">
                            <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                Live Transcript
                            </Text>
                            
                            {liveTranscript ? (
                                <div className="bg-neutral-50 p-4 rounded">
                                    <Text className="text-lg text-neutral-900 font-medium">
                                        "{liveTranscript}"
                                    </Text>
                                    <Text className="text-xs text-neutral-900 opacity-60 mt-2">
                                        {new Date().toLocaleTimeString()}
                                    </Text>
                                </div>
                            ) : (
                                <div className="bg-neutral-50 p-4 rounded text-center">
                                    <Text className="text-neutral-900 opacity-60">
                                        {isEnabled ? 'Speak to see live transcript...' : 'Enable microphone to start testing'}
                                    </Text>
                                </div>
                            )}
                        </div>

                        {/* Recent Transcripts */}
                        {pendingTranscripts.length > 0 && (
                            <div className="bg-neutral-0 hairline p-6">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                    Recent Transcripts ({pendingTranscripts.length})
                                </Text>
                                <div className="space-y-3 max-h-64 overflow-y-auto">
                                    {pendingTranscripts.map((transcript) => (
                                        <div key={transcript.id} className="bg-neutral-50 p-3 rounded">
                                            <Text className="text-sm text-neutral-900 font-medium">
                                                "{transcript.text}"
                                            </Text>
                                            <Text className="text-xs text-neutral-900 opacity-60 mt-1">
                                                {new Date(transcript.timestamp).toLocaleTimeString()}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="bg-neutral-0 hairline p-6">
                            <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                How to Test
                            </Text>
                            <div className="space-y-3 text-sm text-neutral-900">
                                <div className="flex items-start">
                                    <div className="w-6 h-6 bg-neutral-900 text-neutral-0 rounded-full flex items-center justify-center text-xs font-mono mr-3 mt-0.5">1</div>
                                    <div>
                                        <Text className="font-medium">Enable Microphone Testing</Text>
                                        <Text className="text-xs opacity-60">Click "Enable Microphone Testing" to start</Text>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-6 h-6 bg-neutral-900 text-neutral-0 rounded-full flex items-center justify-center text-xs font-mono mr-3 mt-0.5">2</div>
                                    <div>
                                        <Text className="font-medium">Say Wake Phrase</Text>
                                        <Text className="text-xs opacity-60">Say "hey chef" to activate speech recognition</Text>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-6 h-6 bg-neutral-900 text-neutral-0 rounded-full flex items-center justify-center text-xs font-mono mr-3 mt-0.5">3</div>
                                    <div>
                                        <Text className="font-medium">Check Settings</Text>
                                        <Text className="text-xs opacity-60">Click "Settings" to view engine status and switch engines</Text>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Settings Modal */}
                <ChefVoiceSettings
                    speechManager={speechManager}
                    isOpen={showSettings}
                    onClose={() => setShowSettings(false)}
                    onSettingsChange={handleSettingsChange}
                    currentEngine={currentEngine}
                    engineStatuses={engineStatuses}
                    micOn={micOn}
                    statusMessage={statusMessage}
                    triggerDetected={triggerDetected}
                    onEngineSwitch={async (engineType) => {
                        if (speechManager && speechManager.smartEngine) {
                            const success = await speechManager.smartEngine.switchEngine(engineType);
                            if (success) {
                                setCurrentEngine(engineType);
                                setStatusMessage(`Switched to ${engineType} engine`);
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ChefVoiceDemo;
